import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	useGetPendingOnboardingQuery,
	useDismissStepMutation,
	useDismissAllMutation,
} from "@/pages/Private/redux/featureOnboarding/featureOnboarding.api";
import {
	featureOnboardingSelector,
	selectCurrentStep,
	selectIsLastStep,
	selectStepProgress,
} from "@/pages/Private/redux/featureOnboarding/featureOnboarding.slice";

import { SpotlightOverlay } from "./SpotlightOverlay";

export const OnboardingTour: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`featureOnboarding.${key}`, options);
	const location = useLocation();
	const navigate = useNavigate();

	const isFeatureActive = useIsFeatureActive(FeaturesCodes.FEATURE_ONBOARDING);

	// Only fetch when the feature flag is active (avoids 403)
	useGetPendingOnboardingQuery(undefined, { skip: !isFeatureActive });

	const { currentGroup } = useAppSelector(featureOnboardingSelector);
	const currentStep = useAppSelector(selectCurrentStep);
	const isLastStep = useAppSelector(selectIsLastStep);
	const { current, total } = useAppSelector(selectStepProgress);

	const [dismissStep] = useDismissStepMutation();
	const [dismissAll] = useDismissAllMutation();

	const [targetEl, setTargetEl] = useState<Element | null>(null);

	// Re-check for target element when route changes or step changes
	const findTarget = useCallback(() => {
		if (!currentStep) {
			setTargetEl(null);

			return;
		}

		const el = document.querySelector(currentStep.targetSelector);

		setTargetEl(el);
	}, [currentStep]);

	useEffect(() => {
		findTarget();
	}, [findTarget, location.pathname]);

	// Also re-check after a short delay to account for DOM rendering
	useEffect(() => {
		if (!currentStep) {
			return;
		}

		const timer = setTimeout(findTarget, 500);

		return () => clearTimeout(timer);
	}, [currentStep, findTarget, location.pathname]);

	const handleNext = useCallback(async () => {
		if (!currentStep || !currentGroup) {
			return;
		}

		await dismissStep(currentStep.id);

		if (currentStep.ctaRoute) {
			navigate(currentStep.ctaRoute);
		}
		// Step index advancement is handled by the slice's extraReducer for dismissStep.matchFulfilled
	}, [currentStep, currentGroup, dismissStep, navigate]);

	const handleSkip = useCallback(async () => {
		if (!currentGroup) {
			return;
		}

		await dismissAll(currentGroup.featureCode);
	}, [currentGroup, dismissAll]);

	// Don't render if no data, no step, or target not found
	if (!isFeatureActive || !currentGroup || !currentStep || !targetEl) {
		return null;
	}

	return (
		<SpotlightOverlay placement={currentStep.placement} target={targetEl}>
			<h3 className="mb-2 text-base font-semibold text-gray-900">{currentStep.title}</h3>
			<p className="mb-4 text-sm text-gray-600">{currentStep.description}</p>
			<div className="flex items-center justify-between">
				<button
					className="text-sm font-medium text-gray-500 hover:text-gray-700 cursor-pointer"
					type="button"
					onClick={handleSkip}
				>
					{ts("skipTour")}
				</button>
				<div className="flex items-center gap-3">
					<span className="text-xs text-gray-400">{ts("stepOf", { current, total })}</span>
					<Button
						color={ButtonColor.BRAND}
						size={ButtonSize.S}
						title={currentStep.ctaLabel ?? (isLastStep ? ts("gotIt") : ts("next"))}
						type="button"
						onClick={handleNext}
					/>
				</div>
			</div>
		</SpotlightOverlay>
	);
};
